/*******************************************

* [COMPONENT]: CART DRAWER

  * Internal events:
    * - l:cart:updated: Fired when the cart is updated
    * - l:cart:discount:applied: Fired when a discount code is applied
  * External events
    * - l:product:added: Fired when a product is added to the cart
    * - l:cart:error: Fired when a cart error occurs
    *   ++ add 2 items into cart while product has only 1 item
********************************************/

class LTAMainCart extends HTMLElement {
	constructor() {
		super()

		this.updatedVariants = []
		this._onQuantityChange = l_debounce(this._onQuantityChange.bind(this), 200)
		this._onRemoveLineItem = this._onRemoveLineItem.bind(this)
		this._onCartUpdate = this._onCartUpdate.bind(this)
	}

	connectedCallback() {
		this.updatedVariants = []

		this.addEventListener('change', this._onQuantityChange)
		this.addEventListener('click', this._onRemoveLineItem)

		document.addEventListener('l:product:added', this._onCartUpdate)
		document.addEventListener('l:product:updated', this._onCartUpdate)
		document.addEventListener('l:cart:error', this._onCartUpdate)
		document.addEventListener('l:cart:discount:applied', this._onCartUpdate)
	}

	_onRemoveLineItem(event) {
		const removeBtn = event.target.closest('[data-remove-line-item]')
		if (!removeBtn) return

		event.preventDefault()

		const lineItemEl = event.target.closest('[data-drawer-line-item]')
		if (lineItemEl) {
			this._updateCartLine(lineItemEl, 0)
		}
	}

	_onQuantityChange(event) {
		const lineItemEl = event.target.closest('[data-drawer-line-item]')

		if (!lineItemEl) return

		const quantity = parseInt(event.target.value || 1, 10)

		this._updateCartLine(lineItemEl, quantity)
	}

	_updateCartLine(lineItemEl, quantity) {
		if (!lineItemEl) return

		this.classList.add('is-loading')

		const line = parseInt(lineItemEl.dataset.index, 10)
		const variantId = lineItemEl.dataset.variantId

		ltaCartService
			.changeCartLineItem({
				line,
				quantity,
				sections_url: window.location.pathname,
				sections: this._getSectionsToRender().map((section) => section.id),
			})
			.then((cart) => {
				if (cart?.status === 422) {
					this._handleCartError(cart, lineItemEl)

					return
				}

				this._updateCartData(cart.sections[this.dataset.sectionId])

				this.dispatchEvent(new CustomEvent('l:cart:updated', { detail: { cart }, bubbles: true }))
				this.updatedVariants.push(variantId)
			})
			.catch((error) => {
				console.error(error)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_handleCartError(error = { description: '' }, lineItemEl) {
		// Add error message to line item
		const errorMessageEl = lineItemEl.querySelector('[data-error-message]')

		if (errorMessageEl) {
			errorMessageEl.innerHTML = error.description
			errorMessageEl.parentElement.classList.remove('l-hidden')
		}

		// Dispatch event
		// this.dispatchEvent(
		// 	new CustomEvent('l:cart:error', {
		// 		detail: { id: 'cart-drawer', description: error.description },
		// 		bubbles: true,
		// 	})
		// )
	}

	_onCartUpdate() {
		this.classList.add('is-loading')

		fetch(`${window.LTATheme.routes.cart_url}?section_id=${this.dataset.sectionId}`)
			.then((res) => res.text())
			.then((html) => {
				this._updateCartData(html)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_updateCartData(html) {
		const newCartDrawerHTML = l_getSectionDOM(html, '#main-cart')
		this.classList.toggle('is-empty', newCartDrawerHTML?.classList.contains('is-empty'))

		const selectors = [
			'[data-cart-header]',
			'[data-left-side]',
			'[data-cart-inner]',
			'[data-cart-subtotal]',
			'[data-cart-footer]',
			'[data-free-shipping-bar]',
			'[data-discount-code-container]',
		]

		selectors.forEach((selector) => {
			const newSection = newCartDrawerHTML?.querySelector(selector)
			const oldSection = this.querySelector(selector)

			if (newSection && oldSection) oldSection.innerHTML = newSection.innerHTML
		})
	}

	_getSectionsToRender() {
		return [
			{
				id: this.dataset.sectionId,
				selector: '.shopify-section',
			},
		]
	}
}

customElements.define('l-main-cart', LTAMainCart)

/*******************************************
 * [COMPONENT]: SHIPPING BAR
 ********************************************/

if (!customElements.get('l-cart-thresholds')) {
	class LTACartThresholds extends HTMLElement {
		constructor() {
			super()
			this.progressBarEl = null
			this.thresholdsEl = null
			this.labelEl = null
			this.data = null
		}

		connectedCallback() {
			this.progressBarEl = this.querySelector('[data-progress-bar]')
			this.thresholdsEl = this.querySelector('[data-thresholds]')
			this.labelEl = this.querySelector('[data-label]')
			this._initialize()
		}

		_initialize() {
			this._parseData()
			this._updateCart()
		}

		_parseData() {
			const scriptTag = this.querySelector('script[type="application/json"]')
			const parsedData = this.data || (scriptTag ? JSON.parse(scriptTag.textContent) : {})
			const { cartTotal = 0, cartCurrency, shopCurrency, thresholds = [] } = parsedData

			const isCurrencyDifferent = cartCurrency !== shopCurrency

			const normalizeThreshold = (threshold) => ({
				...threshold,
				threshold: parseFloat(threshold.threshold),
			})

			const convertThreshold = (threshold) => ({
				...threshold,
				threshold: Currency.convert(parseFloat(threshold.threshold), shopCurrency, cartCurrency),
			})

			let convertedCartTotal = parseFloat(cartTotal)
			// [PAA] Only keep enabled thresholds — disabled ones skewed maxThreshold/steps
			// and crashed the label render once the last enabled threshold was reached
			let convertedThresholds = thresholds.filter((t) => t.enabled).map(normalizeThreshold)

			if (isCurrencyDifferent) {
				convertedCartTotal = Currency.convert(cartTotal, shopCurrency, cartCurrency)
				convertedThresholds = thresholds.filter((t) => t.enabled).map(convertThreshold)
			}

			const maxThreshold = convertedThresholds.length ? Math.max(...convertedThresholds.map((t) => t.threshold)) : 1 // Prevent division by zero

			const activeThresholds = convertedThresholds.filter((t) => t.enabled && convertedCartTotal >= t.threshold)

			const currentProgress = convertedCartTotal / maxThreshold
			const currentProgressPercentage = Math.min(currentProgress * 100, 100)

			this.data = {
				...parsedData,
				cartTotal: convertedCartTotal,
				thresholds: convertedThresholds,
				maxThreshold,
				activeThresholdsCount: activeThresholds.length,
				currentProgressPercentage,
			}
		}

		_render() {
			this._updateProgressBar()
			this._renderThresholds()
			this._renderLabel()
		}

		_updateProgressBar() {
			// [PAA] Continuous fill driven by the cart total (was stepped per threshold reached),
			// so "you're $X away" progress is visible between $0 and the goal
			const { currentProgressPercentage } = this.data
			const percentage = Math.min(Math.max(currentProgressPercentage, 6), 100)

			if (this.progressBarEl) {
				this.progressBarEl.style.setProperty('--l-progress-bar-width', `${percentage}%`)
				this.progressBarEl.setAttribute('aria-valuenow', Math.round(percentage))
			}
		}

		_renderThresholds() {
			const { thresholds, cartTotal } = this.data
			let thresholdHTML = '<div><div></div></div>'

			thresholds.forEach(({ enabled, label, threshold }) => {
				const isActive = cartTotal >= threshold
				thresholdHTML += `
					<div class="l-cdShippingBar__threshold l-flex-center l-flex-col l-text-center">
						<span class="l-cdShippingBar__threshold-circle l-sq l-inline-flex l-block-spaces${isActive ? ' is-active' : ''}"></span>
						<span class="l-cdShippingBar__threshold-label l-text l-block-spaces${isActive ? ' is-active' : ''}">${label}</span>
						<span class="l-cdShippingBar__threshold-money l-text">${l_formatMoney(threshold)}</span>
					</div>
				`
			})

			if (this.thresholdsEl) {
				// [PAA] Single-goal bars pin the marker to the end of the bar (see paa-cart-drawer.css)
				this.thresholdsEl.classList.toggle('is-single', thresholds.length === 1)
				this.thresholdsEl.innerHTML = thresholdHTML
			}
		}

		_renderLabel() {
			// [PAA] Rewritten around "next unreached threshold" — the old sorted[1] lookup
			// threw once a single enabled threshold was crossed (disabled gift threshold case)
			const { successMessage, progressMessage, cartTotal, thresholds } = this.data

			let notificationText = successMessage

			if (thresholds.length) {
				const sorted = [...thresholds].sort((a, b) => a.threshold - b.threshold)
				const nextThreshold = sorted.find((t) => cartTotal < t.threshold)

				if (nextThreshold) {
					notificationText = progressMessage
						.replace('[money]', l_formatMoney(nextThreshold.threshold - cartTotal))
						.replace('[name]', nextThreshold.label)
				}
			}

			if (this.labelEl && notificationText) {
				this.labelEl.innerHTML = notificationText

				// Mirror the visible label for screen readers (progressbar description)
				const srOnlyEl = this.querySelector('[data-sr-only]')
				if (srOnlyEl) srOnlyEl.textContent = this.labelEl.textContent
			}
		}

		_updateCart() {
			const { cartTotal, thresholds } = this.data

			const freeGiftCard = thresholds.find((t) => t.variantId && t.enabled)

			let needToUpdateCart = false
			let cartUpdateObj = null

			// Need to add freeGiftCard
			if (freeGiftCard?.itemsInCart == 0 && freeGiftCard?.threshold <= cartTotal) {
				cartUpdateObj = { [freeGiftCard.variantId]: 1 }
			}

			// Need to remove
			if (freeGiftCard?.itemsInCart > 0 && freeGiftCard?.threshold > cartTotal) {
				cartUpdateObj = { [freeGiftCard.variantId]: 0 }
			}

			if (!cartUpdateObj) {
				this._render()

				return
			}

			ltaCartService
				.updateCartLineItems(cartUpdateObj)
				.then((response) => {
					console.log(response)
				})
				.catch((error) => {
					console.error(error)
				})
				.finally(() => {
					this._render()
				})
		}
	}

	customElements.define('l-cart-thresholds', LTACartThresholds)
}

/*******************************************
 * [COMPONENT]: DISCOUNT CODE
 ********************************************/

class LTAMainCartDiscountCode extends HTMLElement {
	constructor() {
		super()
		this.handleSubmit = this.handleSubmit.bind(this)
		this.handleInput = l_debounce(this.handleInput.bind(this), 200)
	}

	connectedCallback() {
		this.inputEl = this.querySelector('[data-discount-input]')
		this.submitEl = this.querySelector('[data-submit]')
		this.removeDiscountEls = this.querySelectorAll('[data-remove-discount]')
		this.cartDrawerEl = document.getElementById('cart-drawer')

		this.discountCodeCount = parseInt(this.dataset.discountCodeCount, 10) || 0
		this.discountCodes = (this.dataset.discountCodeCodes || '').split(',').filter(Boolean)

		this.addRemoveDiscountListeners()
		this.submitEl?.addEventListener('click', this.handleSubmit)
		this.inputEl?.addEventListener('input', this.handleInput)
	}

	handleInput() {
		if (this.submitEl && this.inputEl) {
			this.submitEl.disabled = this.inputEl.value.length === 0
		}
	}

	handleSubmit(event) {
		event.preventDefault()

		this.cartDrawerEl?.classList.add('is-loading')
		this.submitEl?.classList.add('is-loading')

		const codes = Array.from(new Set([...(this.discountCodes || []), this.inputEl?.value || ''])).filter(Boolean)
		const discount = codes.join(',')

		this.updateCodes(discount)
	}

	addRemoveDiscountListeners() {
		this.removeDiscountEls.forEach((removeDiscountEl) => {
			removeDiscountEl.addEventListener('click', () => {
				removeDiscountEl.classList.add('l-button-loading')
				const discountCode = removeDiscountEl.dataset.removeDiscount
				const newDiscountCodes = this.discountCodes.filter((code) => code !== discountCode).join(',')
				this.updateCodes(newDiscountCodes)
			})
		})
	}

	updateCodes(discount) {
		fetch(`${window.LTATheme.routes.cart_update_url}`, {
			...l_fetchConfig(),
			body: JSON.stringify({ discount }),
		})
			.then((response) => response.json())
			.then((response) => {
				this.dispatchEvent(
					new CustomEvent('l:cart:discount:applied', {
						detail: { response },
						bubbles: true,
					})
				)
			})
			.finally(() => {
				l_debounce(() => {
					this.submitEl?.classList.remove('is-loading')
					if (this.inputEl) this.inputEl.value = ''
				}, 500)()
			})
	}
}

customElements.define('l-cart-discount-code', LTAMainCartDiscountCode)

/*******************************************
 * [COMPONENT]: CART NOTE
 ********************************************/

// class LTACartNote extends HTMLElement {
// 	constructor() {
// 		super()
// 		this.handleInput = l_debounce(this.handleInput.bind(this), 400)
// 	}

// 	connectedCallback() {
// 		this.textAreaEl = this.querySelector('textarea')
// 		if (this.textAreaEl) {
// 			this.textAreaEl.addEventListener('input', this.handleInput)
// 		}
// 	}

// 	handleInput() {
// 		const cartNote = this.textAreaEl?.value || ''

// 		fetch(window.LTATheme.routes.cart_update_url, {
// 			...l_fetchConfig(),
// 			body: JSON.stringify({ note: cartNote }),
// 		}).then((response) => response.json())
// 	}
// }

// customElements.define('l-cart-note', LTACartNote)

/**********************************
 * [PRODUCT BLOCK]: UPSELL PRODUCT
 ***********************************/

if (!customElements.get('l-cart-upsell-products')) {
	customElements.define(
		'l-cart-upsell-products',
		class LTACartUpsellProducts extends HTMLElement {
			constructor() {
				super()

				this._onProductAdded = this._onProductAdded.bind(this)
				this._onCartUpdated = this._onCartUpdated.bind(this)
				this._onDrawerOpen = this._onDrawerOpen.bind(this)
			}

			connectedCallback() {
				// [PAA] Inside the cart drawer: lazy-load on first open (no request on page load)
				// and let the drawer's section re-render handle cart updates.
				this.drawerEl = this.closest('l-cart-drawer')

				if (this.drawerEl && !this.drawerEl.classList.contains('is-active')) {
					document.addEventListener('l:drawer:open', this._onDrawerOpen)

					return
				}

				this.loadRecommendations(this.dataset.defaultProductId)

				if (!this.drawerEl) {
					document.addEventListener('l:product:added', this._onProductAdded)
					document.addEventListener('l:cart:updated', this._onCartUpdated)
				}
			}

			disconnectedCallback() {
				// [PAA] Clean up so replaced elements stop fetching recommendations
				document.removeEventListener('l:drawer:open', this._onDrawerOpen)
				document.removeEventListener('l:product:added', this._onProductAdded)
				document.removeEventListener('l:cart:updated', this._onCartUpdated)
			}

			_onDrawerOpen(event) {
				if (event.detail.drawerName !== this.drawerEl?.dataset.drawerName) return

				document.removeEventListener('l:drawer:open', this._onDrawerOpen)
				this.loadRecommendations(this.dataset.defaultProductId)
			}

			_onProductAdded(event) {
				// [PAA] /cart/add.js line items expose product_id (id is the variant id)
				const response = event.detail.response
				const productId = response?.items?.[0]?.product_id || response?.product_id

				this.loadRecommendations(productId)
			}

			_onCartUpdated(event) {
				const productId = event.detail.cart?.items?.[0]?.product_id

				this.loadRecommendations(productId)
			}

			async loadRecommendations(productId) {
				// [PAA] Nothing to recommend against (empty cart / missing id)
				if (!productId) return

				fetch(`${this.dataset.url.replace('#productId', productId)}`)
					.then((res) => res.text())
					.then((html) => {
						const newCartDrawerHTML = l_getSectionDOM(html, '#shopify-section-a-ajax-cart-upsell-products')

						if (newCartDrawerHTML) {
							this.querySelector('[data-products]').innerHTML = newCartDrawerHTML.innerHTML
						}
					})
					.finally(() => {
						this.classList.remove('is-loading')
					})
			}
		}
	)
}

